<ng-container [ngSwitch]="data.element">
  <app-modal-header *ngSwitchCase="'playground'" title="What is the playground?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'integration'" title="What is an integration?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'extractor'" title="What is the extractor tool?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'computation'" title="What is a transformation?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'template'" title="What is a template?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'workflow'" title="What is a workflow?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'dashboard-workflows-run'" title="Workflow executed" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'dashboard-tasks-executed'" title="Tasks executed" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'files'" title="What are files?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'board'" title="What is the Task Board?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'engine'" title="What is an engine?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'segment'" title="What is a segment?" (onClose)="dialogRef.close()"></app-modal-header>
  <app-modal-header *ngSwitchCase="'voice'" title="What is a voice?" (onClose)="dialogRef.close()"></app-modal-header>
</ng-container>

<mat-dialog-content>
  <ng-container [ngSwitch]="data.element">
    <div *ngSwitchCase="'playground'">
      <div class="text-gray-400 text-sm">
        <p>
          The <strong>Onion Playground</strong> is a safe, interactive space to test your data transformations without sending anything to your live tools.
        </p>
        <p>
          You can load and preview up to <strong>25 items</strong> from your data source. Instantly see how your transformation settings affect each item — tweak, iterate, and fine-tune until everything looks just right.
        </p>
        <p>
          This environment runs your <strong>data sources</strong> and <strong>transformations</strong>, but <strong>does not trigger any destinations</strong> (like CRMs, APIs, or databases), so you can experiment freely without risk.
        </p>
        <p>
          Whether you're technical or not, the Playground helps you confidently build and validate your workflow before going live.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'integration'">
      <div class="text-gray-400 text-sm">
        <p>
          Setting up integrations is the first step in building with Onion.
        </p>
        <p>
          Connect the apps you want to use, like a CRM, database, or analytics tool. Once connected, these integrations become available inside the workflow builder or the extractor tool, where you can use them to pull in or send out data as part of your automation.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'extractor'">
      <div class="text-gray-400 text-sm">
        <p>
          A Data Extractor pulls specific information from one of your connected apps at regular intervals.
        </p>
        <p>
          After connecting your integration, you can configure the extractor by selecting the data source, applying filters, and setting the schedule for how often the data should be fetched.
        </p>
        <p>
          This feature is a powerful add-on that, in many cases, can replace more complex and expensive reverse-ETL tools. It's designed to be simple to set up, but flexible enough to handle advanced use cases.
        </p>
        <p>
          When combined with a workflow, the extractor becomes even more powerful, allowing you to not only fetch data but also transform and send it wherever you need. Together, they cover a wide range of automation needs, from simple syncs to enterprise-grade pipelines.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'computation'">
      <div class="text-gray-400 text-sm">
        <p>
          Transformations let you manipulate data directly inside your workflow: from combining fields and calculating metrics, to formatting values and applying business logic. They’re useful when you need to clean or enrich data before sending it: number elsewhere.
        </p>
        <p>
          Onion includes a wide range of built-in transformation functions, and for advanced cases, you can write custom logic using JavaScript. These modules are reusable across workflows, making it easy to maintain consistent logic at scale.
        </p>
        <p>
          One of Onion’s key differentiators is the ability to apply these transformations on the fly: no need to set up a separate pipeline or external processing layer. Everything happens right inside the workflow, keeping things fast, transparent, and easy to maintain.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'template'">
      <div class="text-gray-400 text-sm">
        <p>
          Templates are pre-built workflows you can load with a single click. They’re the fastest way to get started with Onion, no need to build from scratch.
        </p>
        <p>
          Each template includes a full automation setup, often with data transformations already wired in. Just plug in your credentials, adjust the filters or mappings, and you’re good to go.
        </p>
        <p>
          Templates help you move fast, avoid mistakes, and follow proven patterns for popular use cases. Perfect for both beginners and power users looking to save time.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'workflow'">
      <div class="text-gray-400 text-sm">
        <p>
          A workflow is the core building block in Onion: it defines the full automation logic from start to finish.
        </p>
        <p>
          It starts by receiving data from a source like a Data Extractor job, a webhook, an external system, or a scheduled cron. Data sources and transformations are optional, and you can add as many as needed.
        </p>
        <p>
          Workflows give you full control over how data moves and evolves, all in a single place. No step limits, no hidden costs.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'dashboard-workflows-run'">
      <div class="text-gray-400 text-sm">
        <p>
          Each run is a complete automation (from extraction to load) finished within your current billing period.
        </p>
        <p>
          It shows how many times your workflows have actually moved data end-to-end on Onion.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'dashboard-tasks-executed'">
      <div class="text-gray-400 text-sm">
        <p>
          Every task is a real operation: a data request, an AI step, or a destination push. Together, they show how much your workflows are actually getting done.
        </p>
        <p>
          One workflow can run dozens of tasks, all billed as a single execution. This metric reflects the real activity happening inside Onion.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'files'">
      <div class="text-gray-400 text-sm">
        <p>
          Files let you upload <strong>custom data or documents</strong> directly into Onion — things like PDFs, CSVs, JSON files, or images.
        </p>
        <p>
          Once uploaded, they become <strong>reusable assets</strong> inside your workflows. You can attach them to GPT transformations as context, include them in API calls, or load them as data sources for processing.
        </p>
        <p>
          Each file stays in your workspace, securely stored and ready to use whenever you need it. You can update or remove them at any time, just like any other resource.
        </p>
        <p>
          Think of Files as your personal <strong>data vault</strong> — always accessible, ready to plug into any workflow without needing an external upload or URL.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'board'">
      <div class="text-gray-400 text-sm">
        <p>
          The <strong>Task Board</strong> is a shared Kanban-style space for tracking GTM tasks, client feedback, and campaign changes, without leaving Onion.
        </p>
        <p>
          Cards move through columns like <strong>To do</strong>, <strong>In progress</strong>, <strong>Awaiting feedback</strong>, and <strong>Done</strong>. Each card can carry a due date, priority, assignees, and a comment thread, so context stays attached to the work instead of scattered across emails or Slack.
        </p>
        <p>
          Since operators and clients can share access to the same board, it doubles as a lightweight way to keep everyone aligned on what's in progress and what's next.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'engine'">
      <div class="text-gray-400 text-sm">
        <p>
          An <strong>engine</strong> groups multiple workflows together under a shared goal, so related automations stay organized instead of scattered across your workspace.
        </p>
        <p>
          Engines can define shared <strong>properties</strong>, like a list of job titles or a limit, that your workflows reference directly from a simple form, without needing to dig into the workflow builder to tweak them.
        </p>
        <p>
          Since an engine bundles its workflows and their extractions together, it can be <strong>exported and imported as a single package</strong>, making it easy to reuse a full automation setup across workspaces or share it with others.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'segment'">
      <div class="text-gray-400 text-sm">
        <p>
          A <strong>segment</strong> is a saved list of contacts or companies defined by a set of conditions, like "companies never scored" or "contacts in sales with no email".
        </p>
        <p>
          The list is <strong>dynamic</strong>: records join and leave it automatically as your data changes, so it always reflects who matches right now.
        </p>
        <p>
          Build a segment once and <strong>reuse it</strong> across your engines and workflows, instead of rebuilding the same filters every time.
        </p>
      </div>
    </div>
    <div *ngSwitchCase="'voice'">
      <div class="text-gray-400 text-sm">
        <p>
          A <strong>voice</strong> describes how your LinkedIn messages should sound: your tone, how polished or casual they are, how hard they sell, the phrases you use and the ones you would never use.
        </p>
        <p>
          When Onion writes a connection request, a first message or a follow-up, it writes it in the voice you pick, so your outreach sounds like you and not like generic AI.
        </p>
        <p>
          You create one with a <strong>short interview</strong>, mostly by clicking, then review and adjust what we understood. A voice isn't tied to a person: most workspaces need one, and some keep two or three for different campaigns.
        </p>
      </div>
    </div>
  </ng-container>

</mat-dialog-content>
